// Feature flags are no longer needed for this service

import { STORAGE_LIMITS } from '@src/constants/storage-limits';
import { getCurrentISOString } from '@src/utils/dateUtils';
import { generateUniqueId } from '@src/utils/idGenerator';

/**
 * エラーレベル
 */
enum ErrorLevel {
  DEBUG = 'debug',
  INFO = 'info',
  WARNING = 'warning',
  ERROR = 'error',
  CRITICAL = 'critical',
}

/**
 * エラーログエントリ
 */
interface ErrorLogEntry {
  timestamp: string;
  level: ErrorLevel;
  message: string;
  error?: Error;
  context?: Record<string, unknown>;
  stack?: string;
  userAgent?: string;
  url?: string;
  userId?: string;
  sessionId?: string;
}

/**
 * エラーログプロバイダーインターフェース
 */
interface ErrorLogProvider {
  log(entry: ErrorLogEntry): Promise<void>;
  flush(): Promise<void>;
}

/**
 * ローカルストレージプロバイダー
 */
class LocalStorageProvider implements ErrorLogProvider {
  private readonly maxEntries = STORAGE_LIMITS.ERROR_LOG.MAX_ENTRIES;
  private readonly storageKey = 'errorLogs';

  async log(entry: ErrorLogEntry): Promise<void> {
    try {
      const logs = this.getStoredLogs();
      logs.push(entry);

      // 最大数を超えたら古いものから削除
      if (logs.length > this.maxEntries) {
        logs.splice(0, logs.length - this.maxEntries);
      }

      localStorage.setItem(this.storageKey, JSON.stringify(logs));
    } catch (error) {
      console.error('Failed to store error log:', error);
    }
  }

  async flush(): Promise<void> {
    localStorage.removeItem(this.storageKey);
  }

  private getStoredLogs(): ErrorLogEntry[] {
    try {
      const stored = localStorage.getItem(this.storageKey);
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  }

  getRecentLogs(count: number = 10): ErrorLogEntry[] {
    const logs = this.getStoredLogs();
    return logs.slice(-count);
  }
}

/**
 * コンソールプロバイダー（開発環境用）
 */
class ConsoleProvider implements ErrorLogProvider {
  async log(entry: ErrorLogEntry): Promise<void> {
    const style = this.getLogStyle(entry.level);
    console.log(`%c[${entry.level.toUpperCase()}] ${entry.timestamp}`, style, entry.message);

    if (entry.error) {
      console.error(entry.error);
    }

    if (entry.context) {
      console.log('Context:', entry.context);
    }
  }

  async flush(): Promise<void> {
    console.clear();
  }

  private getLogStyle(level: ErrorLevel): string {
    const styles = {
      [ErrorLevel.DEBUG]: 'color: gray',
      [ErrorLevel.INFO]: 'color: blue',
      [ErrorLevel.WARNING]: 'color: orange; font-weight: bold',
      [ErrorLevel.ERROR]: 'color: red; font-weight: bold',
      [ErrorLevel.CRITICAL]: 'color: white; background: red; font-weight: bold; padding: 2px 4px',
    };
    return styles[level] || '';
  }
}

/**
 * エラーログ管理クラス
 */
class ErrorLogger {
  private providers: ErrorLogProvider[] = [];
  private sessionId: string;
  private userId?: string;
  private initialized = false;

  constructor() {
    this.sessionId = this.generateSessionId();
  }

  private initializeProviders() {
    if (this.initialized) {
      return;
    }
    this.initialized = true;

    // 開発環境ではコンソールに出力
    if (import.meta.env.DEV) {
      this.providers.push(new ConsoleProvider());
    }

    // ローカルストレージには常に保存
    this.providers.push(new LocalStorageProvider());
  }

  /**
   * エラーログ記録
   */
  async logError(
    error: Error | string,
    context?: Record<string, any>,
    level: ErrorLevel = ErrorLevel.ERROR
  ): Promise<void> {
    this.initializeProviders();

    const entry: ErrorLogEntry = {
      timestamp: getCurrentISOString(),
      level,
      message: typeof error === 'string' ? error : error.message,
      error: error instanceof Error ? error : undefined,
      stack: error instanceof Error ? error.stack : undefined,
      context,
      userAgent: navigator.userAgent,
      url: window.location.href,
      userId: this.userId,
      sessionId: this.sessionId,
    };

    // すべてのプロバイダーにログを送信
    await Promise.all(this.providers.map((provider) => provider.log(entry).catch(console.error)));
  }

  /**
   * 情報ログ
   */
  async logInfo(message: string, context?: Record<string, any>): Promise<void> {
    await this.logError(message, context, ErrorLevel.INFO);
  }

  /**
   * 警告ログ
   */
  async logWarning(message: string, context?: Record<string, any>): Promise<void> {
    await this.logError(message, context, ErrorLevel.WARNING);
  }

  /**
   * デバッグログ
   */
  async logDebug(message: string, context?: Record<string, any>): Promise<void> {
    this.initializeProviders();
    if (import.meta.env.DEV) {
      await this.logError(message, context, ErrorLevel.DEBUG);
    }
  }

  /**
   * 重大エラーログ
   */
  async logCritical(error: Error | string, context?: Record<string, any>): Promise<void> {
    await this.logError(error, context, ErrorLevel.CRITICAL);
  }

  /**
   * ユーザーIDの設定
   */
  setUserId(userId: string | undefined) {
    this.userId = userId;
  }

  /**
   * 最近のログ取得（開発用）
   */
  getRecentLogs(count: number = 10): ErrorLogEntry[] {
    this.initializeProviders();
    const provider = this.providers.find(
      (p) => p instanceof LocalStorageProvider
    ) as LocalStorageProvider;
    return provider ? provider.getRecentLogs(count) : [];
  }

  /**
   * ログのクリア
   */
  async clearLogs(): Promise<void> {
    this.initializeProviders();
    await Promise.all(this.providers.map((provider) => provider.flush().catch(console.error)));
  }

  /**
   * セッションIDの生成
   */
  private generateSessionId(): string {
    return generateUniqueId('session');
  }

  /**
   * パフォーマンス計測
   */
  measurePerformance<T>(label: string, fn: () => T): T {
    const start = performance.now();
    let result: T;
    let error: unknown;

    try {
      result = fn();
    } catch (e) {
      error = e;
      const duration = performance.now() - start;
      this.logDebug(`Performance: ${label}`, {
        duration: `${duration.toFixed(2)}ms`,
        success: false,
      });
      throw error;
    }

    const duration = performance.now() - start;

    this.logDebug(`Performance: ${label}`, {
      duration: `${duration.toFixed(2)}ms`,
      success: true,
    });

    return result;
  }

  /**
   * 非同期パフォーマンス計測
   */
  async measureAsyncPerformance<T>(label: string, fn: () => Promise<T>): Promise<T> {
    const start = performance.now();
    let result: T | undefined;
    let error: unknown;

    try {
      result = await fn();
    } catch (e) {
      error = e;
      const duration = performance.now() - start;
      await this.logDebug(`Async Performance: ${label}`, {
        duration: `${duration.toFixed(2)}ms`,
        success: false,
      });
      throw error;
    }

    const duration = performance.now() - start;

    await this.logDebug(`Async Performance: ${label}`, {
      duration: `${duration.toFixed(2)}ms`,
      success: true,
    });

    return result as T;
  }
}

// シングルトンインスタンス
export const errorLogger = new ErrorLogger();

/**
 * グローバルエラーハンドラーの設定
 */
function _setupGlobalErrorHandlers() {
  // 未処理のPromiseエラー
  window.addEventListener('unhandledrejection', (event) => {
    errorLogger.logError(
      new Error(`Unhandled Promise Rejection: ${event.reason}`),
      { reason: event.reason },
      ErrorLevel.ERROR
    );
  });

  // グローバルエラー
  window.addEventListener('error', (event) => {
    errorLogger.logError(
      event.error || new Error(event.message),
      {
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
      },
      ErrorLevel.ERROR
    );
  });
}

/**
 * React Error Boundary用のヘルパー
 */
function _logComponentError(error: Error, componentStack: string, componentName?: string) {
  errorLogger.logError(
    error,
    {
      componentStack,
      componentName,
      errorBoundary: true,
    },
    ErrorLevel.ERROR
  );
}
